import { useState, useEffect } from 'react';
import { Controller } from 'react-hook-form';
import {
  Select,
  IconArrowRight,
  BaseellaStatus,
  BaseellaStatusTitle,
  SelectListPosition,
  InputText,
  SelectItemProps,
  PaymentType,
  SelectType,
  InputTime,
  Autocomplete,
  Currency,
  apiAbortController,
} from '@ui/src';
import { ProcessingRulesFormTypes } from './types';
import {
  CurrencyTypeEnum,
  ProcessingTypeEnum,
  TimeScheduleEnum,
  PspParterAccountSelectTypeEnum,
} from '../../../hoc/transation-settings/processing-rules/types';

import cn from './form.module.css';

let timeout: NodeJS.Timeout;

const statusesList = [
  {
    name: BaseellaStatusTitle.DRAFT,
    value: BaseellaStatus.DRAFT,
  },
  {
    name: BaseellaStatusTitle.NONE,
    value: BaseellaStatus.NONE,
  },
  {
    name: BaseellaStatusTitle.NEW,
    value: BaseellaStatus.NEW,
  },
  {
    name: BaseellaStatusTitle.PROC,
    value: BaseellaStatus.PROC,
  },
  {
    name: BaseellaStatusTitle.SENT,
    value: BaseellaStatus.SENT,
  },
  {
    name: BaseellaStatusTitle.ARCH,
    value: BaseellaStatus.ARCH,
  },
  {
    name: BaseellaStatusTitle.REV,
    value: BaseellaStatus.REV,
  },
  {
    name: BaseellaStatusTitle.AML,
    value: BaseellaStatus.AML,
  },
  {
    name: BaseellaStatusTitle.AML_INFO,
    value: BaseellaStatus.AML_INFO,
  },
  {
    name: BaseellaStatusTitle.AML_REJ,
    value: BaseellaStatus.AML_REJ,
  },
  {
    name: BaseellaStatusTitle.AML_DONE,
    value: BaseellaStatus.AML_DONE,
  },
  {
    name: BaseellaStatusTitle.REJECTED,
    value: BaseellaStatus.REJECTED,
  },
  {
    name: BaseellaStatusTitle.NOT_SIGNED,
    value: BaseellaStatus.NOT_SIGNED,
  },
  {
    name: BaseellaStatusTitle.READY_TO_SEND,
    value: BaseellaStatus.READY_TO_SEND,
  },
  {
    name: BaseellaStatusTitle.READY_TO_PSP,
    value: BaseellaStatus.READY_TO_PSP,
  },
  {
    name: BaseellaStatusTitle.ACCP_OUT,
    value: BaseellaStatus.ACCP_OUT,
  },
  {
    name: BaseellaStatusTitle.UNDEFINED_FUNDS,
    value: BaseellaStatus.UNDEFINED_FUNDS,
  },
  {
    name: BaseellaStatusTitle.FX_RATE_REQUEST,
    value: BaseellaStatus.FX_RATE_REQUEST,
  },
  {
    name: BaseellaStatusTitle.REFUNDED,
    value: BaseellaStatus.REFUNDED,
  },
  {
    name: BaseellaStatusTitle.BOOKED,
    value: BaseellaStatus.BOOKED,
  },
  {
    name: BaseellaStatusTitle.HELD,
    value: BaseellaStatus.HELD,
  },
];

const ProcessingRuleForm = ({
  control,
  errors,
  pspIntegrations,
  pspPartners,
  currencies,
  currencyType,
  pspIntegrationType,
  pspPartnerType,
  setValue,
  currencyId,
  transactionType,
  paymentTypes,
  paymentCategoryId,
  casp,
  caspId,
  ruleId,
  transactionCurrenciesIds,
  timeSchedule,
  pspParterAccountSelectType,
  pspPartnerAccounts,
  pspPartnerId,
  onSearchPspPartnerAccounts,
  pspPartnerAccountsIsLoading,
  processingType,
  onClearPspAccount,
  getAutocompleteListAction,
  autocompleteListPagination,
  nextPageIsLoading,
  transactionTypes,
  pspPartnerAccountId,
}: ProcessingRulesFormTypes) => {
  const [availableCurrencies, setAvailableCurrencies] = useState<
    SelectItemProps[]
  >([]);
  const [selectedCurrencies, setSelectedCurrencies] = useState<string[]>([]);

  const outgoingTransactionType = transactionTypes?.find(
    (item) => item.name === 'OUTGOING',
  );

  useEffect(() => {
    if (paymentTypes && paymentCategoryId) {
      const currentPaymentType = paymentTypes?.find(
        (item) => item.id === paymentCategoryId,
      );

      if (currentPaymentType) {
        if (
          currentPaymentType.paymentType !== PaymentType.CRYPTO_ASSET_PAYMENT
        ) {
          setAvailableCurrencies(
            currentPaymentType?.currencies?.edges?.map((item) => ({
              name: item.node.name,
              value: item.node.id,
            })),
          );
        } else if (
          currentPaymentType.paymentType === PaymentType.CRYPTO_ASSET_PAYMENT &&
          caspId &&
          casp
        ) {
          const currentCasp = casp?.find((item) => item.id === caspId);

          if (currentCasp) {
            setAvailableCurrencies(
              currentCasp?.currencies?.map((item) => ({
                name: item.currency.name,
                value: item.currencyId,
              })),
            );
          }
        }
      }
    }
  }, [paymentTypes, paymentCategoryId, casp, caspId]);

  useEffect(() => {
    setSelectedCurrencies(transactionCurrenciesIds as string[]);
  }, [transactionCurrenciesIds]);

  return (
    <div className={cn.processingRuleForm}>
      <div
        className={cn.formGroup}
        style={{ zIndex: 18, position: 'relative' }}
      >
        <Controller
          control={control}
          defaultValue=""
          name="active"
          render={({ field: { value, name, onChange } }) => (
            <Select
              name={name}
              value={value as string}
              title="Rule status"
              placeholder="Select status"
              onChange={onChange}
              error={errors?.active?.message}
              list={[
                {
                  name: 'Active',
                  value: 'true',
                },
                {
                  name: 'Inactive',
                  value: 'false',
                },
              ]}
              fullWidth
            />
          )}
        />
      </div>
      <div
        className={cn.formGroup}
        style={{ zIndex: 17, position: 'relative' }}
      >
        <Controller
          control={control}
          defaultValue=""
          name="typeId"
          render={({ field: { value, name, onChange } }) => (
            <Select
              name={name}
              value={value}
              title="Transaction type"
              placeholder="Select transaction type"
              onChange={onChange}
              error={errors?.typeId?.message}
              list={transactionTypes
                ?.filter((item) => item.name !== 'INTERNAL_PAYMENT')
                ?.map((item) => ({
                  name: item.name
                    .split('_')
                    .map(
                      (typeName) =>
                        `${typeName.charAt(0)}${typeName.slice(1).toLowerCase()}`,
                    )
                    .join(' '),
                  value: item.id,
                }))}
              fullWidth
            />
          )}
        />
      </div>
      <div className={cn.processingRuleFormTitle}>Conditions</div>
      <div
        className={cn.formGroup}
        style={{ zIndex: 16, position: 'relative' }}
      >
        <Controller
          control={control}
          defaultValue=""
          name="processingType"
          render={({ field: { value, name, onChange } }) => (
            <Select
              name={name}
              value={value}
              title="Processing type"
              placeholder="Select processing type"
              onChange={(e) => {
                onChange(e);
                setValue('fromStatus', '');
                setValue('toStatus', '');
              }}
              error={errors?.processingType?.message}
              list={[
                {
                  name: 'Minimum amount check',
                  value: ProcessingTypeEnum.MINIMUM_AMOUNT_CHECK,
                },
                {
                  name: 'PSP Auto Select',
                  value: ProcessingTypeEnum.PSP_AUTO_SELECT,
                  hidden: transactionType !== outgoingTransactionType?.id,
                },
              ]}
              fullWidth
            />
          )}
        />
      </div>
      <div
        className={`${cn.formGroup} ${cn.formGroupFlex}`}
        style={{ zIndex: 15, position: 'relative' }}
      >
        <div className={cn.formGroupItem}>
          <Controller
            control={control}
            defaultValue=""
            name="amount"
            render={({ field: { value, name, onChange } }) => (
              <InputText
                name={name}
                value={value}
                title="Amount"
                placeholder="Enter amount"
                onChange={onChange}
                error={errors?.amount?.message}
                className={cn.processingRuleAmount}
                currency={
                  currencyId
                    ? currencies?.find((item) => item.id === currencyId)
                    : ({ decimals: 2 } as Currency)
                }
                prefix={
                  <div className={cn.processingRuleAmountPrefix}>{'<'}</div>
                }
                prefixClassName={cn.processingRuleAmountPrefixWrapper}
                amountInput
              />
            )}
          />
        </div>
        <div className={cn.formGroupItem}>
          <Controller
            control={control}
            defaultValue=""
            name="currencyId"
            render={({ field: { value, name, onChange } }) => (
              <Select
                name={name}
                value={value as string}
                title="Amount Currency"
                placeholder="Select currency"
                onChange={onChange}
                error={errors?.currencyId?.message}
                list={currencies?.map((item) => ({
                  name: item.name,
                  value: item.id,
                }))}
                fullWidth
              />
            )}
          />
        </div>
      </div>
      {currencyId && (
        <div className={cn.processingRuleAmountCurrencyHint}>
          The amount will be automatically converted if the transaction currency
          differs from{' '}
          {currencies?.find((item) => item.id === currencyId)?.name}.
        </div>
      )}
      <div
        className={cn.formGroup}
        style={{ zIndex: 14, position: 'relative' }}
      >
        <Controller
          control={control}
          defaultValue=""
          name="pspIntegrationType"
          render={({ field: { value, name, onChange } }) => (
            <Select
              name={name}
              value={value}
              title="Integration type"
              placeholder="Select integration type"
              onChange={(e) => {
                onChange(e);

                if (e.target.value === CurrencyTypeEnum.ALL) {
                  setValue('pspIntegrationId', '');
                }
              }}
              error={errors?.pspIntegrationType?.message}
              list={[
                {
                  name: 'All types',
                  value: CurrencyTypeEnum.ALL,
                },
                {
                  name: 'Specific',
                  value: CurrencyTypeEnum.SPECIFIC,
                },
              ]}
              fullWidth
            />
          )}
        />
      </div>
      {pspIntegrationType === CurrencyTypeEnum.SPECIFIC && (
        <div
          className={cn.formGroup}
          style={{ zIndex: 13, position: 'relative' }}
        >
          <Controller
            control={control}
            defaultValue=""
            name="pspIntegrationId"
            render={({ field: { value, name, onChange } }) => (
              <Select
                name={name}
                value={value as string}
                title="Integration (optional)"
                placeholder="Select integration"
                onChange={onChange}
                error={errors?.pspIntegrationId?.message}
                list={pspIntegrations?.map((item) => ({
                  name: item.name
                    .split('_')
                    .map(
                      (nameValue) =>
                        `${nameValue.charAt(0)}${nameValue
                          .slice(1)
                          .toLowerCase()}`,
                    )
                    .join(' '),
                  value: item.value,
                }))}
                fullWidth
              />
            )}
          />
        </div>
      )}
      {pspIntegrationType !== CurrencyTypeEnum.SPECIFIC &&
        errors?.pspIntegrationId?.message && (
          <div className={cn.formError}>
            {errors?.pspIntegrationId?.message}
          </div>
        )}
      <div
        className={cn.formGroup}
        style={{ zIndex: 12, position: 'relative' }}
      >
        <Controller
          control={control}
          defaultValue=""
          name="paymentCategoryId"
          render={({ field: { value, name, onChange } }) => (
            <Select
              name={name}
              value={value as string}
              title="Payment type"
              placeholder="Select payment type"
              onChange={onChange}
              error={errors?.paymentCategoryId?.message}
              list={paymentTypes?.map((item) => ({
                name: item.name,
                value: item.id,
              }))}
              fullWidth
            />
          )}
        />
      </div>
      {paymentTypes?.find((item) => item.id === paymentCategoryId)
        ?.paymentType === PaymentType.CRYPTO_ASSET_PAYMENT && (
        <div
          className={cn.formGroup}
          style={{ zIndex: 11, position: 'relative' }}
        >
          <Controller
            control={control}
            defaultValue=""
            name="caspId"
            render={({ field: { value, name, onChange } }) => (
              <Select
                name={name}
                value={value as string}
                title="CASP"
                placeholder="Select CASP"
                onChange={(e) => {
                  onChange(e.target.value);

                  setValue('currenciesIds', []);
                }}
                error={errors?.caspId?.message}
                list={casp
                  ?.filter((item) => (ruleId ? item : item.active))
                  ?.map((item) => ({
                    name: item.name,
                    value: item.id,
                  }))}
                fullWidth
              />
            )}
          />
        </div>
      )}

      <div
        className={cn.formGroup}
        style={{ zIndex: 10, position: 'relative' }}
      >
        <Controller
          control={control}
          defaultValue=""
          name="currencyType"
          render={({ field: { value, name, onChange } }) => (
            <Select
              name={name}
              value={value}
              title="Currency type"
              placeholder="Select currency type"
              onChange={(e) => {
                onChange(e);

                if (e.target.value === CurrencyTypeEnum.ALL) {
                  setSelectedCurrencies([]);
                }
              }}
              error={errors?.currencyType?.message}
              list={[
                {
                  name: 'All currencies',
                  value: CurrencyTypeEnum.ALL,
                },
                {
                  name: 'Specific',
                  value: CurrencyTypeEnum.SPECIFIC,
                },
              ]}
              fullWidth
            />
          )}
        />
      </div>
      {currencyType === CurrencyTypeEnum.SPECIFIC && (
        <div
          className={cn.formGroup}
          style={{ zIndex: 9, position: 'relative' }}
        >
          <Controller
            control={control}
            defaultValue={[]}
            name="currenciesIds"
            render={({ field: { name, onChange } }) => (
              <Select
                name={name}
                value={selectedCurrencies}
                title="Transaction currencies"
                placeholder="Select currencies"
                onChange={(e) => {
                  clearTimeout(timeout);

                  setSelectedCurrencies(e.target.value as string[]);
                  apiAbortController.abortPendingRequest();

                  timeout = setTimeout(() => {
                    onChange(e);
                  }, 500);
                }}
                error={errors?.currenciesIds?.message}
                list={availableCurrencies}
                mode={SelectType.MULTIPLE}
                disabled={Boolean(
                  !paymentCategoryId ||
                  (paymentCategoryId &&
                    paymentTypes?.find((item) => item.id === paymentCategoryId)
                      ?.paymentType === PaymentType.CRYPTO_ASSET_PAYMENT &&
                    !caspId),
                )}
                fullWidth
              />
            )}
          />
        </div>
      )}
      {currencyType !== CurrencyTypeEnum.SPECIFIC &&
        errors?.currenciesIds?.message && (
          <div className={cn.formError}>{errors?.currenciesIds?.message}</div>
        )}

      {transactionCurrenciesIds && transactionCurrenciesIds?.length > 0 && (
        <div className={cn.transactionCurrenciesHint}>
          Rule applies only to transactions in these currencies
        </div>
      )}
      {(transactionType === outgoingTransactionType?.id ||
        processingType === ProcessingTypeEnum.MINIMUM_AMOUNT_CHECK) && (
        <>
          {transactionType === outgoingTransactionType?.id && (
            <>
              <div className={cn.formGroup}>
                <Controller
                  control={control}
                  defaultValue=""
                  name="timeWindowType"
                  render={({ field: { value, name, onChange } }) => (
                    <Select
                      name={name}
                      value={value}
                      title="Time schedule"
                      placeholder="Select schedule"
                      onChange={onChange}
                      error={errors?.timeWindowType?.message}
                      list={[
                        {
                          name: 'Full Day',
                          value: TimeScheduleEnum.FULL_DAY,
                        },
                        {
                          name: 'Specific time',
                          value: TimeScheduleEnum.SPECIFIC_TIME_WINDOW,
                        },
                      ]}
                      fullWidth
                    />
                  )}
                />
              </div>
              {timeSchedule === TimeScheduleEnum.SPECIFIC_TIME_WINDOW && (
                <div className={`${cn.formGroup} ${cn.formGroupFlex}`}>
                  <div className={cn.formGroupItem}>
                    <Controller
                      control={control}
                      defaultValue=""
                      name="timeWindowStart"
                      render={({ field: { value, name, onChange } }) => (
                        <InputTime
                          name={name}
                          value={value as string}
                          placeholder="00:00"
                          onChange={onChange}
                          title="Start time (UTC)"
                          error={errors?.timeWindowStart?.message}
                        />
                      )}
                    />
                  </div>
                  <div className={cn.formGroupItem}>
                    <Controller
                      control={control}
                      defaultValue=""
                      name="timeWindowEnd"
                      render={({ field: { value, name, onChange } }) => (
                        <InputTime
                          name={name}
                          value={value as string}
                          placeholder="00:00"
                          onChange={onChange}
                          title="End time (UTC)"
                          error={errors?.timeWindowEnd?.message}
                        />
                      )}
                    />
                  </div>
                </div>
              )}
            </>
          )}
        </>
      )}
      <div className={cn.processingRuleFormTitle}>Action</div>
      <div className={`${cn.formGroup} ${cn.formGroupFlex}`}>
        <div className={cn.formGroupItem}>
          <Controller
            control={control}
            defaultValue=""
            name="fromStatus"
            render={({ field: { value, name, onChange } }) => (
              <Select
                name={name}
                value={value}
                title="From status"
                placeholder="Select status"
                onChange={onChange}
                error={errors?.fromStatus?.message}
                list={statusesList?.filter((item) =>
                  processingType === ProcessingTypeEnum.MINIMUM_AMOUNT_CHECK
                    ? item.value === BaseellaStatus.NOT_SIGNED
                    : item,
                )}
                selectListPosition={SelectListPosition.TOP}
                disabled={Boolean(!transactionType || !processingType)}
              />
            )}
          />
        </div>
        <div className={cn.iconWrapper}>
          <IconArrowRight width={16} height={16} />
        </div>
        <div className={cn.formGroupItem}>
          <Controller
            control={control}
            defaultValue=""
            name="toStatus"
            render={({ field: { value, name, onChange } }) => (
              <Select
                name={name}
                value={value}
                title="To status"
                placeholder="Select status"
                onChange={onChange}
                error={errors?.toStatus?.message}
                list={statusesList?.filter((item) =>
                  processingType === ProcessingTypeEnum.MINIMUM_AMOUNT_CHECK
                    ? item.value === BaseellaStatus.REJECTED ||
                      item.value === BaseellaStatus.HELD
                    : item,
                )}
                selectListPosition={SelectListPosition.TOP}
                disabled={Boolean(!transactionType || !processingType)}
              />
            )}
          />
        </div>
      </div>
      {transactionType !== outgoingTransactionType?.id && (
        <>
          <div className={cn.formGroup}>
            <Controller
              control={control}
              defaultValue=""
              name="pspPartnerType"
              render={({ field: { value, name, onChange } }) => (
                <Select
                  name={name}
                  value={value}
                  title="Bank Partner type"
                  placeholder="Select bank partner type"
                  onChange={(e) => {
                    onChange(e);

                    if (e.target.value === CurrencyTypeEnum.ALL) {
                      setValue('pspPartnerId', '');
                    }
                  }}
                  error={errors?.pspPartnerType?.message}
                  list={[
                    {
                      name: 'All partners',
                      value: CurrencyTypeEnum.ALL,
                    },
                    {
                      name: 'Specific',
                      value: CurrencyTypeEnum.SPECIFIC,
                    },
                  ]}
                  fullWidth
                />
              )}
            />
          </div>
          {pspPartnerType === CurrencyTypeEnum.SPECIFIC && (
            <div className={cn.formGroup}>
              <Controller
                control={control}
                defaultValue=""
                name="pspPartnerId"
                render={({ field: { value, name, onChange } }) => (
                  <Select
                    name={name}
                    value={value as string}
                    title="Bank partner (optional)"
                    placeholder="Select bank partner"
                    onChange={onChange}
                    error={errors?.pspPartnerId?.message}
                    list={pspPartners}
                    fullWidth
                  />
                )}
              />
            </div>
          )}
          {pspPartnerType !== CurrencyTypeEnum.SPECIFIC &&
            errors?.pspPartnerId?.message && (
              <div className={cn.formError}>
                {errors?.pspPartnerId?.message}
              </div>
            )}
        </>
      )}
      {transactionType === outgoingTransactionType?.id &&
        processingType === ProcessingTypeEnum.PSP_AUTO_SELECT && (
          <>
            <div className={cn.formGroup}>
              <Controller
                control={control}
                defaultValue=""
                name="pspPartnerAccountSelectType"
                render={({ field: { value, name, onChange } }) => (
                  <Select
                    name={name}
                    value={value as string}
                    title="Selection Type"
                    placeholder="Select type"
                    onChange={(e) => {
                      onChange(e);

                      if (
                        e.target.value ===
                        PspParterAccountSelectTypeEnum.FROM_EXTERNAL_ACCOUNT
                      ) {
                        setValue('pspPartnerId', '');
                      }
                    }}
                    error={errors?.pspPartnerAccountSelectType?.message}
                    list={[
                      {
                        name: 'Specific',
                        value: PspParterAccountSelectTypeEnum.SPECIFIC,
                      },
                      {
                        name: 'From External Account',
                        value:
                          PspParterAccountSelectTypeEnum.FROM_EXTERNAL_ACCOUNT,
                      },
                    ]}
                    fullWidth
                  />
                )}
              />
            </div>
            {(pspParterAccountSelectType ===
              PspParterAccountSelectTypeEnum.SPECIFIC ||
              pspParterAccountSelectType ===
                PspParterAccountSelectTypeEnum.FROM_EXTERNAL_ACCOUNT) && (
              <>
                <div className={cn.formGroup}>
                  <Controller
                    control={control}
                    defaultValue=""
                    name="pspPartnerId"
                    render={({ field: { value, name, onChange } }) => (
                      <Select
                        name={name}
                        value={value as string}
                        title="Bank partner"
                        placeholder="Select bank partner"
                        onChange={(e) => {
                          onChange(e);

                          if (!e.target.value && pspPartnerAccountId) {
                            setValue('pspPartnerAccountId', '');
                          }
                        }}
                        error={errors?.pspPartnerId?.message}
                        list={pspPartners}
                        disabled={!pspParterAccountSelectType}
                        selectListPosition={SelectListPosition.TOP}
                        withClearButton
                        fullWidth
                      />
                    )}
                  />
                </div>

                <div className={cn.formGroup}>
                  <Controller
                    control={control}
                    defaultValue=""
                    name="pspPartnerAccountId"
                    render={({ field: { value, name, onChange } }) => (
                      <Autocomplete
                        name={name}
                        value={value as string}
                        title="Bank Partner Account"
                        placeholder={
                          pspParterAccountSelectType !==
                          PspParterAccountSelectTypeEnum.SPECIFIC
                            ? 'Auto selection'
                            : 'Select bank partner account'
                        }
                        onChange={onChange}
                        error={errors?.pspPartnerAccountId?.message}
                        onSearch={onSearchPspPartnerAccounts}
                        list={pspPartnerAccounts}
                        isLoading={pspPartnerAccountsIsLoading}
                        selectListPosition={SelectListPosition.TOP}
                        disabled={Boolean(!pspPartnerId)}
                        onClearClick={() => {
                          if (ruleId) {
                            onClearPspAccount();
                          }
                        }}
                        onInfinateScroll={() => {
                          getAutocompleteListAction(
                            autocompleteListPagination?.endCursor as string,
                          );
                        }}
                        nextPageIsLoading={nextPageIsLoading}
                        hasNextPage={autocompleteListPagination?.hasNextPage}
                        withInfiniteScroll
                        withClearButton
                        fullWidth
                      />
                    )}
                  />
                </div>
              </>
            )}
            {pspParterAccountSelectType !==
              PspParterAccountSelectTypeEnum.SPECIFIC &&
              transactionType === outgoingTransactionType?.id &&
              processingType === ProcessingTypeEnum.PSP_AUTO_SELECT &&
              errors?.pspPartnerId?.message && (
                <div className={cn.formError}>
                  {errors?.pspPartnerId?.message}
                </div>
              )}
            {pspParterAccountSelectType !==
              PspParterAccountSelectTypeEnum.SPECIFIC &&
              transactionType === outgoingTransactionType?.id &&
              processingType === ProcessingTypeEnum.PSP_AUTO_SELECT &&
              errors?.pspPartnerAccountId?.message && (
                <div className={cn.formError}>
                  {errors?.pspPartnerAccountId?.message}
                </div>
              )}
          </>
        )}
    </div>
  );
};

export default ProcessingRuleForm;
